/**
 * In-app confirmation dialog (module dialog).
 * Replaces native window.confirm so destructive actions stay in the product UI.
 *
 * Built on the native `<dialog>` element via CortexDialog.
 */

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Button } from '@coordio/ui';
import { CortexDialog, CortexDialogBody, CortexDialogFooter } from '../dialogs/CortexDialog';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmColor?: 'primary' | 'secondary' | 'error' | 'warning';
}

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: 'primary' | 'secondary' | 'error' | 'warning';
}

/** Color mapping from semantic name to Tailwind classes */
const CONFIRM_COLOR_CLASS: Record<string, string> = {
  error: 'bg-red-600 hover:bg-red-700 text-white',
  warning: 'bg-amber-600 hover:bg-amber-700 text-white',
  primary: '',
  secondary: '',
};

/**
 * Confirmation dialog — native `<dialog>` via CortexDialog.
 */
export const ConfirmDialog = ({
  open,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  confirmColor = 'error',
}: ConfirmDialogProps) => {
  return (
    <CortexDialog
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      data-testid="confirm-dialog"
    >
      <CortexDialogBody>
        <p className="text-sm text-muted-foreground">{message}</p>
      </CortexDialogBody>
      <CortexDialogFooter>
        <Button variant="outline" size="sm" onClick={onCancel}>
          {cancelText}
        </Button>
        <Button
          size="sm"
          className={CONFIRM_COLOR_CLASS[confirmColor] ?? ''}
          onClick={onConfirm}
          data-testid="confirm-dialog-confirm"
        >
          {confirmText}
        </Button>
      </CortexDialogFooter>
    </CortexDialog>
  );
};

const ConfirmDialogContext = createContext<((options: ConfirmOptions) => Promise<boolean>) | null>(
  null,
);

/**
 * Provides a promise-based confirm() that opens the shared module dialog.
 */
export const ConfirmDialogProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({ title: '', message: '' });
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((opts: ConfirmOptions) => {
    resolveRef.current?.(false);
    setOptions(opts);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const finish = useCallback((value: boolean) => {
    setOpen(false);
    resolveRef.current?.(value);
    resolveRef.current = null;
  }, []);

  return (
    <ConfirmDialogContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={open}
        title={options.title}
        message={options.message}
        confirmText={options.confirmText ?? 'Delete'}
        cancelText={options.cancelText}
        confirmColor={options.confirmColor ?? 'error'}
        onConfirm={() => finish(true)}
        onCancel={() => finish(false)}
      />
    </ConfirmDialogContext.Provider>
  );
};

/**
 * Opens the shared confirmation dialog. Resolves true if the user confirms.
 */
export function useConfirmDialog() {
  const confirm = useContext(ConfirmDialogContext);
  if (!confirm) {
    throw new Error('useConfirmDialog must be used within ConfirmDialogProvider');
  }
  return confirm;
}
